/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import { Grid, Typography } from '@mui/material';

import {
  getInitialState,
  step1ValidationSchema,
  step2ValidationSchema,
} from '../../../../containers/CreateOrganizationOverlay/CreateOrganizationOverlay.consts';
import {
  CreateOrganizationApiValidationErrors,
  CreateOrganizationValues,
  ResetApiErrorForField,
} from '../../../../containers/CreateOrganizationOverlay/CreateOrganizationOverlay.types';
import { Namespaces } from '../../../../i18n';
import { OrganizationApiService } from '../../../../services/OrganizationApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { DataState } from '../../../../types/common/DataState';
import { CreateOrganizationRequest } from '../../../../types/organization/CreateOrganizationRequest';
import { dataError, dataLoading, isDataLoading } from '../../../../utils/dataState';
import { unprocessibleEntityToObject } from '../../../../utils/unprocessibleEntityToObject';
import { useObjectState } from '../../../../utils/useObjectState';
import { useSetupPageStyles } from '../../SetupPage.styles';
import { OrganizationForm } from './components/OrganizationForm';

const connector = connect();
type PropsFromRedux = ConnectedProps<typeof connector>;

type OrganizationStepProps = {
  onOrganizationCreate: () => void;
} & PropsFromRedux;

type State = {
  dataState: DataState;
  errorMessage?: string;
  apiErrors: CreateOrganizationApiValidationErrors;
};

export const OrganizationStepComponent = memo<OrganizationStepProps>(props => {
  const { onOrganizationCreate } = props;
  const { t } = useTranslation(Namespaces.Setup);
  const classes = useSetupPageStyles();

  const [state, setState] = useObjectState<State>({
    dataState: DataState.notAsked,
    apiErrors: {},
  });
  const { dataState, apiErrors } = state;

  const initialValues: CreateOrganizationValues = useMemo(() => getInitialState(), []);
  const validationSchema = step1ValidationSchema.concat(step2ValidationSchema);

  const submit = async (values: CreateOrganizationValues) => {
    setState({
      dataState: dataLoading(),
      errorMessage: undefined,
      apiErrors: {},
    });
    try {
      const data: CreateOrganizationRequest = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        email: values.email.trim() || undefined,
        companyName: values.companyName.trim(),
        language: values.language.trim(),
        vatNumber: values.vatNumber.trim() || undefined,
        address: {
          line1: values.address.line1.trim(),
          line2: values.address.line2?.trim() || undefined,
          line3: values.address.line3?.trim() || undefined,
          postalCode: values.address.postalCode.trim(),
          city: values.address.city.trim(),
          state: values.address.state.trim() || undefined,
          countryCode: values.address.countryCode,
        },
      };
      const { thumbnail } = values;
      const result = await OrganizationApiService.createOrganization(data);
      if (thumbnail) {
        try {
          await OrganizationApiService.updateOrganizationThumbnail(
            result.data.payload.id,
            thumbnail.type === 'base64'
              ? { type: 'base64', base64: thumbnail.base64 }
              : { type: 'seed', seed: thumbnail.seed },
          );
        } catch (e) {
          /* empty */
        }
      }
      onOrganizationCreate();
    } catch (e) {
      const error = e as ApiClientError;
      const responseData = error.response?.data;
      if (!responseData) {
        setState({ dataState: dataError(), errorMessage: undefined });
      } else {
        const errorMessage = responseData.message;
        if (errorMessage === 'unprocessable_entity') {
          setState({
            dataState: dataError(),
            apiErrors: unprocessibleEntityToObject(responseData.payload),
          });
        } else {
          setState({ dataState: dataError(), errorMessage });
        }
      }
    }
  };

  const skip = () => {
    onOrganizationCreate();
  };

  const isFetching = isDataLoading(dataState);

  const resetApiError: ResetApiErrorForField = useCallback(
    name => {
      if (apiErrors[name]) {
        const nextErrors = { ...apiErrors };
        delete nextErrors[name];
        setState({ apiErrors: nextErrors });
      }
    },
    [apiErrors, setState],
  );

  return (
    <Grid container className={classes.root}>
      <Grid item xs={6} className={classes.description}>
        <Typography data-cy="setup-organization-form-title" variant="h1">
          {t('page.organization.title')}
        </Typography>
        <Typography variant="body1">{t('page.organization.description')}</Typography>
      </Grid>
      <Grid item xs={6} className={classes.formWrapper}>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
          {formProps => (
            <OrganizationForm
              {...formProps}
              resetApiError={resetApiError}
              apiErrors={apiErrors}
              isFetching={isFetching}
              onSkip={skip}
            />
          )}
        </Formik>
      </Grid>
    </Grid>
  );
});

export const OrganizationStep = connector(OrganizationStepComponent);
